import { type FC } from 'react';
import { useUnit } from 'effector-react';
import { Pagination, ScrollArea, Stack } from '@mantine/core';
import { useNavigate } from 'react-router';
import {
    $isPayoutTraderActive,
    usePayoutOrdersList,
    type PayoutOrder,
    type PayoutOrdersFilters,
} from '~/entities/payout';
import { type PaginationModel } from '~/shared/lib/pagination';
import { routes } from '~/shared/constants/routes';
import { getOrderPath, type OrderRouteState } from '~/shared/lib/orderRoutes';
import { Table } from '~/shared/ui/Table';
import { REFETCH_INTERVAL } from './constants';
import { prepareTableData } from './utils';

interface OrdersListProps {
    paginationModel: PaginationModel;
    filters: PayoutOrdersFilters;
}

export const OrdersList: FC<OrdersListProps> = ({ filters, paginationModel }) => {
    const navigate = useNavigate();
    const { pagination, isTraderActive } = useUnit({
        pagination: paginationModel.$pagination,
        isTraderActive: $isPayoutTraderActive,
    });

    const { data: { items, pagination: paginationMeta } = {}, isLoading } = usePayoutOrdersList(
        {
            'filter.publicStatuses': filters.publicStatuses,
            'filter.createdAtGt': filters.createdAtGt,
            'filter.createdAtLt': filters.createdAtLt,
            'filter.orderId.value': filters.orderId?.value,
            'filter.requisiteSuffixes': filters.requisiteSuffixes,
            'pagination.page': pagination.page.toString(),
            'pagination.perPage': pagination.perPage.toString(),
        },
        {
            enabled: isTraderActive,
            refetchInterval: isTraderActive ? REFETCH_INTERVAL : undefined,
        },
    );

    const total = Number(paginationMeta?.total);
    const totalPages = Math.ceil(total / pagination.perPage);

    const tableData = prepareTableData({
        orders: items,
        origin: routes['payout-history'],
    });

    const handleRowClick = (item: PayoutOrder) => {
        const orderId = item.id?.value;

        if (!orderId) {
            return;
        }

        navigate(getOrderPath('payout', orderId), {
            state: { from: routes['payout-history'] } satisfies OrderRouteState,
        });
    };

    const handleChangeCurrentPage = (page: number) => {
        paginationModel.setPage(page - 1);
    };

    return (
        <Stack>
            <ScrollArea h={364}>
                <Table
                    striped
                    highlightOnHover
                    data={tableData}
                    onRowClick={handleRowClick}
                    loading={isLoading}
                />
            </ScrollArea>

            {totalPages > 1 && (
                <Pagination
                    total={totalPages}
                    value={pagination.page + 1}
                    onChange={handleChangeCurrentPage}
                    size='sm'
                />
            )}
        </Stack>
    );
};
